import { useEffect, useRef, useState } from "react";

interface ViatorWidgetProps {
  partnerId: string;
  widgetRef: string;
  minHeight?: string;
  className?: string;
}

export function ViatorWidget({
  partnerId,
  widgetRef,
  minHeight = "400px",
  className = ""
}: ViatorWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(minHeight);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' } // Start loading 200px before it comes into view
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Create the HTML content for the iframe
    const iframeContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          html, body {
            margin: 0;
            padding: 0;
            overflow: hidden;
            background: transparent;
          }
        </style>
      </head>
      <body>
        <div 
          data-vi-partner-id="${partnerId}" 
          data-vi-widget-ref="${widgetRef}"
        ></div>
        <script async src="https://www.viator.com/orion/partner/widget.js"></script>
        <script>
          // Function to send height to parent
          function updateHeight() {
            const height = document.body.scrollHeight;
            window.parent.postMessage({ type: 'viator-height', height: height }, '*');
          }

          // Watch for changes in the body
          const observer = new MutationObserver(updateHeight);
          observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true
          });

          // Also check periodically for the first few seconds while widget loads
          let checks = 0;
          const interval = setInterval(() => {
            updateHeight();
            checks++;
            if (checks > 20) clearInterval(interval); // Stop after 10 seconds
          }, 500);

          // Initial check
          window.addEventListener('load', updateHeight);
        </script>
      </body>
      </html>
    `;

    // Write the content to the iframe
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(iframeContent);
      iframeDoc.close();
    }

    // Listen for height messages from iframe
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'viator-height' && event.data.height) {
        const minPx = parseInt(minHeight, 10) || 400;
        setIframeHeight(`${Math.max(minPx, event.data.height)}px`);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [partnerId, widgetRef, shouldLoad]);

  return (
    <div ref={containerRef} className={className} style={{ minHeight }}>
      {shouldLoad ? (
        <iframe
          ref={iframeRef}
          style={{
            width: '100%',
            height: iframeHeight,
            border: 'none',
            display: 'block'
          }}
          title="Viator Widget"
          data-testid="viator-widget-container"
        />
      ) : (
        <div 
          style={{ 
            width: '100%', 
            height: minHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--muted)'
          }}
        >
          <p style={{ color: 'var(--muted-foreground)' }}>Loading experiences...</p>
        </div>
      )}
    </div>
  );
}
